/**
 * The installed app's identity, from the brand (2026-10-05): name, icon and colours.
 *
 * `brand-public`'s `applyBrandIcons` already points the tab icon and the touch icon at the brand's mark
 * (media's thumbnails of `logo_url`). The web manifest is the third place a page names itself — what a
 * phone shows under an installed app, what Chrome draws on its splash — and until this day every anubis
 * app pointed it at a per-app folder on the `icons` CDN, drawn once by hand: install the learning
 * centre and the home screen said "learn" under a generic glyph, not the brand somebody bought from.
 *
 * So anubis writes the manifest itself, as a `data:` URL built from the brand: its title, its 256px
 * mark and the fleet's bar colour. `start_url` and `scope` are absolute (a `data:` manifest has no
 * base of its own) and are this origin's root. Idempotent: the link is rewritten only when the
 * manifest changes.
 *
 * The bar colour is `PWA_BAR` (owner, 2026-10-29), never the brand's primary: the installed app's
 * status bar and Android navigation bar are drawn from `theme_color` (and the `theme-color` meta),
 * and a primary-coloured frame round every app looked wrong. The splash is the same dark.
 */
import { origin } from '@martincastroalvarez/runtime';

/** What the manifest needs of a brand. Every field optional — a cache may predate any of them. */
export interface ManifestBrand {
  title?: string | null;
  touch_icon_url?: string | null;
}

/** The installed app's status bar, navigation bar and splash, on every app of every tenant. */
export const PWA_BAR = '#181818';

/** Set the `theme-color` meta — the bar colour the browser reads at runtime, over the manifest's. */
function barColour(): void {
  let meta = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  if (meta.content !== PWA_BAR) meta.content = PWA_BAR;
}

/** The manifest a page should carry for `brand`, or `null` when the brand has no mark to install. */
export function brandManifest(
  brand: ManifestBrand | null | undefined,
  origin: string,
): Record<string, unknown> | null {
  const icon = String(brand?.touch_icon_url ?? '').trim();
  if (!icon.startsWith('https://')) return null;
  const name = String(brand?.title ?? '').trim() || new URL(origin).hostname;
  return {
    name,
    short_name: name,
    start_url: `${origin}/`,
    scope: `${origin}/`,
    display: 'standalone',
    icons: [{ src: icon, sizes: '256x256', purpose: 'any' }],
    theme_color: PWA_BAR,
    background_color: PWA_BAR,
  };
}

/** Point the document's manifest at one built from the brand. A no-op without a DOM or a mark. */
export function applyBrandManifest(brand: ManifestBrand | null | undefined): void {
  if (typeof document === 'undefined') return;
  const manifest = brandManifest(brand, origin());
  if (!manifest) return;
  const href = `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`;
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== href) link.setAttribute('href', href);
  barColour();
}

/**
 * An app that is a product of its own rather than a page of the brand (2026-10-18, `wall`): a name
 * and its folder on the fleet's `icons` CDN (`https://icons.<apex>/<app>/`), which holds the
 * favicons, the touch icon and a `site.webmanifest` the `logos` app generated. With one, the tab,
 * the home screen and the installed app say the APP's name under its own icon; the palette and the
 * faces are still the brand's. Without one, the brand's mark is all three — the default.
 *
 * Like isis' `appIconUrl` (2026-10-20): `icons` may be left out, and the folder is then
 * `https://icons.<apex>/<slug>/<version>/` from the tenant's `apex` — the app passes ra's
 * `apexDomain()` (anubis reads no host itself). `version` is the folder's re-art segment (`v2`): icon files are served
 * immutable, so new art gets a new path rather than new bytes at the old one.
 */
export interface AppIdentity {
  name: string;
  /** The app's folder name on the icons CDN; its `name` lower-cased by default. */
  slug?: string | undefined;
  /** The re-art segment, `v2`. */
  version?: string | undefined;
  /** The tenant's apex (`martincastroalvarez.com`), to find the folder by. */
  apex?: string | undefined;
  /** `https://icons.<apex>/<app>/` — trailing slash optional. Found from `apex` when left out. */
  icons?: string | undefined;
}

/**
 * The app's icon folder: `icons` as given, or found from `apex`; `''` when neither says (a dev host).
 *
 * @example appIconsUrl({ name: 'Wall', version: 'v2', apex: 'example.com' })
 *   // 'https://icons.example.com/wall/v2'
 */
export function appIconsUrl(app: AppIdentity): string {
  if (app.icons) return app.icons.replace(/\/+$/, '');
  const apex = (app.apex ?? '').trim().toLowerCase();
  if (!apex.includes('.') || apex.startsWith('localhost')) return '';
  const slug = (app.slug ?? app.name).trim().toLowerCase();
  return `https://icons.${apex}/${slug}${app.version ? `/${app.version}` : ''}`;
}

/**
 * The app's own mark for its bar (a 192px square from its icon folder); `''` without a folder, so
 * the caller falls back to the brand's logo (owner, 2026-10-21: the Wall bar showed the brand's).
 *
 * @example appMarkUrl({ name: 'Wall', version: 'v2', apex: 'example.com' })
 *   // 'https://icons.example.com/wall/v2/android-chrome-192x192.png'
 */
export function appMarkUrl(app: AppIdentity): string {
  const folder = appIconsUrl(app);
  return folder ? `${folder}/android-chrome-192x192.png` : '';
}

function headLink(rel: string, href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== href) link.setAttribute('href', href);
}

/** Point the tab icon, the touch icon and the manifest at the app's own folder. Answers whether it
 * could (a page on `localhost` has no apex to find the folder on). */
export function applyAppIdentity(app: AppIdentity): boolean {
  if (typeof document === 'undefined') return false;
  const base = appIconsUrl(app);
  if (!base.startsWith('https://')) return false;
  headLink('icon', `${base}/favicon-32x32.png`);
  headLink('apple-touch-icon', `${base}/apple-touch-icon.png`);
  headLink('manifest', `${base}/site.webmanifest`);
  barColour();
  return true;
}
